/**
 * StrategyTable.tsx — Procurement table: per-category make/buy toggle, editable
 * internal/materials/work costs + discount %, and a server-computed delta.
 *
 * Unlike the prototype (which computed everything client-side from fixtures),
 * every displayed cost + delta comes from the server StrategyRow. Edits are held
 * in per-row local state and committed on blur / on toggle (never on keystroke)
 * via `onCommit`; the recomputed table flows back through the `rows` prop and
 * the row reconciles to it. BOTH make/buy sides are always sent — the server
 * derives the planned cost from the active side only, so switching Interno ⇄
 * Esterno never erases the other side's numbers.
 *
 * The optimistic local state is only ever a LEASE on the server's values: each
 * row counts its unsettled commits and reconciles from the server row the moment
 * that count returns to 0 (`onCommit` resolves true/false when the PUT settles).
 * That single mechanism covers the three ways optimism used to lie — a rejected
 * save leaving the row advertising a state the server refused, the `v === mode`
 * guard then swallowing the obvious retry, and a queued PUT's row snapping back
 * to a value it has already superseded.
 */
import { useEffect, useRef, useState } from "react";

import { I } from "../../components/icons";
import { fmtEur } from "../../lib/format";
import type { CategoryStrategyInput, MakeBuy, StrategyRow } from "../../lib/strategyApi";

function Toggle({
  value,
  onChange,
}: {
  value: MakeBuy;
  onChange: (v: MakeBuy) => void;
}): JSX.Element {
  const opts: [MakeBuy, string][] = [
    ["internal", "Interno"],
    ["external", "Esterno"],
  ];
  return (
    <div
      style={{
        display: "inline-flex",
        border: "1px solid var(--c-line-strong)",
        borderRadius: 6,
        padding: 2,
        background: "var(--c-bg)",
      }}
    >
      {opts.map(([k, l]) => (
        <button
          key={k}
          // preventDefault on mousedown so clicking the toggle does NOT first
          // blur a focused number input (which would fire a second, stale-mode
          // commit and could race the toggle's own commit).
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange(k)}
          style={{
            appearance: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px 10px",
            fontSize: 11.5,
            fontWeight: 500,
            borderRadius: 4,
            background: value === k ? "var(--c-bg-elev)" : "transparent",
            color: value === k ? "var(--c-ink)" : "var(--c-ink-3)",
            boxShadow: value === k ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
            fontFamily: "inherit",
          }}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function NumInput({
  value,
  onChange,
  onCommit,
  onFocus,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  onCommit: () => void;
  onFocus: () => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <input
      className="input t-num"
      value={disabled ? "—" : value.toLocaleString("it-IT")}
      disabled={disabled}
      onFocus={onFocus}
      onChange={(e) => onChange(Number(e.target.value.replace(/\D/g, "")) || 0)}
      onBlur={onCommit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      style={{
        textAlign: "right",
        height: 28,
        fontSize: 12,
        padding: "0 8px",
        opacity: disabled ? 0.4 : 1,
        fontFamily: "var(--font-mono)",
        background: disabled ? "var(--c-bg-sunken)" : "var(--c-bg-elev)",
        width: "100%",
      }}
    />
  );
}

function PctInput({
  value,
  onChange,
  onCommit,
  onFocus,
}: {
  value: number;
  onChange: (v: number) => void;
  onCommit: () => void;
  onFocus: () => void;
}): JSX.Element {
  return (
    <div style={{ position: "relative" }}>
      <input
        className="input t-num"
        value={value}
        onFocus={onFocus}
        onChange={(e) => onChange(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
        onBlur={onCommit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        style={{
          textAlign: "right",
          height: 28,
          fontSize: 12,
          padding: "0 18px 0 8px",
          fontFamily: "var(--font-mono)",
          width: "100%",
        }}
      />
      <span
        style={{
          position: "absolute",
          right: 7,
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: 11,
          color: "var(--c-ink-3)",
        }}
      >
        %
      </span>
    </div>
  );
}

/** The editable numeric fields of a row, used to track which one has focus. */
type EditField = "internal" | "materials" | "work" | "discount";

/**
 * Whether `field` can be typed into in a given make/buy mode. The server derives
 * the planned cost from the active side only, so the idle side is read-only.
 *
 * This is the SINGLE source of truth for both the inputs' `disabled` props and
 * the revert rule below — deriving them from one predicate is what keeps the two
 * from drifting apart, which is exactly how a value ended up live behind a "—".
 */
function isFieldEnabled(field: EditField, mode: MakeBuy): boolean {
  if (field === "discount") return true; // applies to whichever side is active
  if (field === "internal") return mode === "internal";
  return mode === "external"; // materials + work
}

function StrategyRowView({
  row,
  onCommit,
  onOpenRfq,
}: {
  row: StrategyRow;
  onCommit: (categoryId: string, input: CategoryStrategyInput) => Promise<boolean>;
  onOpenRfq: (row: StrategyRow) => void;
}): JSX.Element {
  const [mode, setMode] = useState<MakeBuy>(row.mode);
  const [internal, setInternal] = useState(row.internal);
  const [materials, setMaterials] = useState(row.materials);
  const [work, setWork] = useState(row.work);
  const [discount, setDiscount] = useState(row.discount);
  // WHICH field of this row currently holds focus, or null. Suppresses the
  // reconcile effect so a server response for a different row/field can't
  // clobber an in-progress edit — and, on the rejection path below, identifies
  // the one field a revert must leave alone.
  const editing = useRef<EditField | null>(null);
  // Commits dispatched from this row that have not settled yet. While > 0 the
  // local optimistic state is the newer truth, so a server row arriving in the
  // meantime (an earlier PUT of ours, or another row's response — every response
  // rewrites the WHOLE table) must not pull the row back to a superseded value.
  const [pending, setPending] = useState(0);
  // The last settled commit was REJECTED and the row has been reverted to the
  // server's values. Without this the revert would be silent, which confuses as
  // much as the stale value it replaces.
  const [unsaved, setUnsaved] = useState(false);
  // Whether the MOST RECENT settlement was a rejection. Written on every settle
  // (not only on failure) so a later success disarms an earlier failure's
  // arming — the last settlement decides, which is the same rule the `pending`
  // guard follows.
  //
  // A rejection OVERRIDES the `editing.current` guard: focusing a field of this
  // row while its own PUT is in flight is ordinary (click the toggle, then go
  // fix a number), and skipping the revert there would leave the row displaying
  // the refused values under a pill that says the opposite — and would make the
  // next blur silently re-send, and persist, the very edit the server refused.
  // The override is narrow: the focused field is exempt from the revert, so it
  // can only ever discard input the failed PUT actually carried.
  const revertOnSettle = useRef(false);

  // Reconcile local edit state from the server row: on any server-value change
  // (our own commit zeroes the inactive side + recomputes) AND whenever `pending`
  // returns to 0. The latter is what reverts an optimistic edit the server
  // rejected — a failed PUT leaves the cache, and therefore every value in the
  // dep list, untouched, so a purely value-keyed effect could never fire. It also
  // restores local `mode` to the server's, which is what makes clicking the same
  // toggle option again a real retry instead of a no-op.
  useEffect(() => {
    // Still queued: the optimistic state is the newer truth. Leave the rejection
    // armed — the LAST settlement decides what the row ends up showing.
    if (pending > 0) return;
    const rejected = revertOnSettle.current;
    revertOnSettle.current = false;
    const focused = editing.current;
    // An uncommitted keystroke outranks a server value that merely CHANGED
    // (another row's response rewrites the whole table). It does not outrank a
    // rejection of this row's own commit — see `revertOnSettle` above.
    if (focused && !rejected) return;
    setMode(row.mode);
    // …but even a rejection leaves the FOCUSED field alone — ON ONE CONDITION:
    // that the field is still typeable once `mode` goes back to the server's.
    // Sparing it is only meaningful while the user can keep typing into it. A
    // refused make/buy switch disables the very field they were in, and a spared
    // value there would be stranded behind a "—": invisible, yet still carried by
    // the next commit (a browser blurs an element it has just disabled, which
    // commits) and still non-zero, which suppresses the seed-on-first-switch that
    // keeps the margin honest. So: spare it only if it survives the revert
    // enabled; otherwise revert it with everything else.
    const spared = focused && isFieldEnabled(focused, row.mode) ? focused : null;
    if (spared !== "internal") setInternal(row.internal);
    if (spared !== "materials") setMaterials(row.materials);
    if (spared !== "work") setWork(row.work);
    if (spared !== "discount") setDiscount(row.discount);
  }, [row.mode, row.internal, row.materials, row.work, row.discount, pending]);

  // Send BOTH sides every time — the server derives the planned cost from the
  // active side only, so the idle side's numbers survive a make/buy switch.
  function buildInput(over?: Partial<CategoryStrategyInput>): CategoryStrategyInput {
    return {
      mode: over?.mode ?? mode,
      internal: over?.internal ?? internal,
      materials: over?.materials ?? materials,
      work: over?.work ?? work,
      discount: over?.discount ?? discount,
    };
  }

  // Switching make/buy. Seed the side we're switching TO when it has never been
  // filled in, so the first switch lands on delta 0 (the "never invent a margin"
  // rule) instead of planning the work at €0 and showing a 100% margin.
  function switchMode(v: MakeBuy): void {
    if (v === mode) return;
    const over: Partial<CategoryStrategyInput> = { mode: v };
    if (v === "internal" && internal === 0) {
      over.internal = row.cmeCost;
      setInternal(row.cmeCost);
    } else if (v === "external" && materials === 0 && work === 0) {
      over.materials = row.cmeCost;
      setMaterials(row.cmeCost);
    }
    setMode(v);
    commit(over);
  }

  function commit(over?: Partial<CategoryStrategyInput>): void {
    editing.current = null;
    // Hold the reconcile off until this PUT settles, then release it — success
    // or failure. `onCommit` never rejects (the page turns a rejection into
    // `false`), so the counter can't leak.
    setPending((n) => n + 1);
    setUnsaved(false);
    void onCommit(row.categoryId, buildInput(over)).then((ok) => {
      revertOnSettle.current = !ok;
      setUnsaved(!ok);
      setPending((n) => n - 1);
    });
  }

  const delta = row.delta;
  const deltaPct = row.deltaPct;

  return (
    <tr>
      <td className="t-mono" style={{ color: "var(--c-ink-3)", fontWeight: 600 }}>
        {row.code ?? "—"}
      </td>
      <td style={{ fontWeight: 500, color: "var(--c-ink)" }}>{row.name}</td>
      <td
        className="t-num"
        style={{ textAlign: "right", fontWeight: 500, fontFamily: "var(--font-mono)" }}
      >
        {fmtEur(row.cmeCost)}
      </td>
      <td>
        <Toggle value={mode} onChange={switchMode} />
      </td>
      <td>
        <NumInput
          value={internal}
          disabled={!isFieldEnabled("internal", mode)}
          onFocus={() => (editing.current = "internal")}
          onChange={setInternal}
          onCommit={() => commit()}
        />
      </td>
      <td>
        <NumInput
          value={materials}
          disabled={!isFieldEnabled("materials", mode)}
          onFocus={() => (editing.current = "materials")}
          onChange={setMaterials}
          onCommit={() => commit()}
        />
      </td>
      <td>
        <NumInput
          value={work}
          disabled={!isFieldEnabled("work", mode)}
          onFocus={() => (editing.current = "work")}
          onChange={setWork}
          onCommit={() => commit()}
        />
      </td>
      <td>
        <PctInput
          value={discount}
          onFocus={() => (editing.current = "discount")}
          onChange={setDiscount}
          onCommit={() => commit()}
        />
      </td>
      <td
        className="t-num"
        style={{
          textAlign: "right",
          fontWeight: 600,
          fontFamily: "var(--font-mono)",
          color: delta > 0 ? "var(--c-ok)" : delta < 0 ? "var(--c-err)" : "var(--c-ink-2)",
        }}
      >
        {delta >= 0 ? "+" : ""}
        {fmtEur(delta)}
        <div style={{ fontSize: 10.5, color: "var(--c-ink-3)", fontWeight: 400 }}>
          {deltaPct >= 0 ? "+" : ""}
          {deltaPct.toFixed(1)}%
        </div>
      </td>
      <td>
        {mode === "external" ? (
          <button className="btn btn-sm" onClick={() => onOpenRfq(row)}>
            <I.Send size={12} /> Invia RFQ
          </button>
        ) : (
          <span className="pill pill-brand" style={{ fontSize: 10.5 }}>
            In-house
          </span>
        )}
        {/* Only once nothing is in flight: while a later commit is still queued
            the row is not settled yet and the label would be premature. */}
        {unsaved && pending === 0 && (
          <span
            className="pill pill-err"
            style={{ fontSize: 10.5, marginLeft: 6 }}
            title="L'ultima modifica è stata rifiutata dal server. I valori mostrati sono quelli salvati."
          >
            Non salvato
          </span>
        )}
      </td>
    </tr>
  );
}

interface StrategyTableProps {
  rows: StrategyRow[];
  /**
   * Dispatch one row's edit. MUST resolve when the PUT settles — `true` if the
   * server accepted it, `false` if it rejected it — and MUST NOT reject: the row
   * releases its reconcile on settlement, so a swallowed promise would freeze it
   * on optimistic state for good.
   */
  onCommit: (categoryId: string, input: CategoryStrategyInput) => Promise<boolean>;
  onOpenRfq: (row: StrategyRow) => void;
}

export function StrategyTable({ rows, onCommit, onOpenRfq }: StrategyTableProps): JSX.Element {
  return (
    <table className="tbl">
      <thead>
        <tr>
          <th style={{ width: 60 }}>Cat.</th>
          <th>Categoria</th>
          <th style={{ width: 130, textAlign: "right" }}>Costo CME</th>
          <th style={{ width: 130 }}>Make / Buy</th>
          <th style={{ width: 110, textAlign: "right" }}>Interno</th>
          <th style={{ width: 130, textAlign: "right" }}>Materiali ext.</th>
          <th style={{ width: 130, textAlign: "right" }}>Lavoro ext.</th>
          <th style={{ width: 80, textAlign: "right" }}>Sconto</th>
          <th style={{ width: 110, textAlign: "right" }}>Delta (€)</th>
          <th style={{ width: 100 }}></th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td
              colSpan={10}
              style={{ textAlign: "center", padding: 28, color: "var(--c-ink-3)", fontSize: 13 }}
            >
              Nessuna categoria — completa prima l&apos;analisi e la categorizzazione
              nell&apos;Audit.
            </td>
          </tr>
        ) : (
          rows.map((r) => (
            <StrategyRowView key={r.categoryId} row={r} onCommit={onCommit} onOpenRfq={onOpenRfq} />
          ))
        )}
      </tbody>
    </table>
  );
}
